import React, { useMemo } from 'react'
import { Spinner } from 'react-bootstrap'
import useHeaderTitle from 'utils/hooks/useHeaderTitle'
import { getFileLink } from 'utils/helpers/file'

import DashboardWrapper from 'components/DashboardWrapper'

import { hasData } from 'utils/helpers'

import styles from './styles.module.scss'
import ResourceCard from './ResourceCard'
import { useResourceLibrary } from './hooks'
import { resourceTypes } from './constant'

const transformResourceData = data => {
  if (!data?.length) return data

  return data.map(resource => {
    const og = resource.ogContent || null
    const isLink = resource?.Resource.type === resourceTypes.LINK

    return {
      id: resource?.Resource.id,
      description: resource?.Resource.description,
      image: og?.ogImage,
      title: resource?.Resource.title,
      url: isLink
        ? resource?.Resource.url
        : getFileLink(resource?.Resource.url),
      sourceName: resource?.Resource.source,
      categoryId: resource?.Resource?.ResourceCategory?.id,
      categoryName: resource?.Resource?.ResourceCategory?.name,
      content: og?.ogDescription,
      siteName: og?.ogSiteName || og?.ogTitle,
      siteIcon: og?.ogFavicon,
      siteImage: og?.ogImage,
      type: resource?.Resource.type
    }
  })
}

const ResourceLibrary = () => {
  useHeaderTitle('Resource Library')

  const {
    resourceLibrary,
    resourceLibraryLoading
  } = useResourceLibrary()

  const recommendedResources = useMemo(
    () => transformResourceData(resourceLibrary?.recommended),
    [resourceLibrary?.recommended]
  )

  const hasCategories = useMemo(() => {
    return !!recommendedResources?.some(r => !!r?.categoryName)
  }, [recommendedResources])

  const groupedResources = useMemo(() => {
    if (!hasData(recommendedResources)) return []

    const map = new Map()
    for (const r of recommendedResources) {
      const key = hasCategories
        ? r.categoryName || 'Uncategorized'
        : 'Resources'
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(r)
    }

    const groups = Array.from(map.entries()).map(
      ([key, resources]) => ({
        key,
        title: key,
        resources
      })
    )

    if (!hasCategories) return groups

    return groups.sort((a, b) => {
      const aUncat = a.key === 'Uncategorized'
      const bUncat = b.key === 'Uncategorized'
      if (aUncat && !bUncat) return 1
      if (!aUncat && bUncat) return -1
      return String(a.key).localeCompare(String(b.key))
    })
  }, [recommendedResources, hasCategories])

  const isEmpty = useMemo(() => {
    return (
      !hasData(recommendedResources) && !resourceLibraryLoading
    )
  }, [recommendedResources, resourceLibraryLoading])

  return (
    <DashboardWrapper className={`${styles.resourceLibrary}`}>
      {resourceLibraryLoading && (
        <Spinner animation="border" variant="primary" />
      )}

      {hasData(recommendedResources) && (
        <div className={styles.resourceGroups}>
          {groupedResources.map(group => (
            <div
              key={group.key}
              className={styles.resourceGroup}
            >
              {hasCategories && (
                <div className={styles.categoryHeader}>
                  {group.title}
                </div>
              )}
              <div className={styles.resourceList}>
                {group.resources.map(
                  ({ id, title, ...rest }) => (
                    <ResourceCard
                      key={id}
                      title={title}
                      recommended
                      layout="row"
                      {...rest}
                    />
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {isEmpty && <p>No resources yet.</p>}
    </DashboardWrapper>
  )
}

ResourceLibrary.propTypes = {}

export default ResourceLibrary
